import { useQuery } from "@tanstack/react-query";

import { queryKeys } from "@/lib/query-keys";
import adminAPI from "@/services/api";

import type { ProceduresSearch } from "../lib/procedures-search";
import { hidesLongClosed, isEnrolledOnly, toListParams } from "../lib/procedures-search";

type ApiProcedureRow = Awaited<ReturnType<typeof adminAPI.listProcedures>>["procedures"][number];
type ApiLegalRegistry = Awaited<ReturnType<typeof adminAPI.getProcedureLegalRegistry>>;

export type ProcedureRow = ReturnType<typeof normalizeProcedure>;
export type ProcedureLegalRegistry = NonNullable<ReturnType<typeof normalizeLegalRegistry>>;
export type LegalRegistryHistoryEntry = ProcedureLegalRegistry["affaireHistorique"][number];
export type ProcedureFilterOptions = Awaited<ReturnType<typeof adminAPI.getProcedureFilterOptions>>;

/** Upper bound of the calendar query — one call marks every upcoming hearing. */
const UPCOMING_HEARINGS_PAGE_SIZE = 500;

/**
 * hapi-swagger cannot express `nullable` on a $ref'd object or a date, so the
 * generated SDK types `legalRegistry`, the historique `document` and every
 * optional date as always present — while the API really sends null for
 * never-synced procedures. Restore the unions here rather than widening the API
 * contract for a front-end typing quirk.
 *
 * `orNull` does the widening: `x ?? null` alone is a no-op when TypeScript
 * already believes `x` is non-nullable.
 *
 * Dates nested inside `legalRegistry` stay as-is — every consumer passes them
 * through `formatDate`, whose parameter already accepts null.
 */
function orNull<T>(value: T | null | undefined): T | null {
  return value ?? null;
}

function normalizeProcedure(procedure: ApiProcedureRow) {
  return {
    ...procedure,
    legalRegistry: orNull(procedure.legalRegistry),
    nextHearing: orNull(procedure.nextHearing),
    courtFillingDate: orNull(procedure.courtFillingDate),
    deliberationDate: orNull(procedure.deliberationDate),
    decisionDate: orNull(procedure.decisionDate),
    legalRegistryPayloadSyncedAt: orNull(procedure.legalRegistryPayloadSyncedAt),
    lastDocumentsSyncAt: orNull(procedure.lastDocumentsSyncAt),
  };
}

function normalizeLegalRegistry(detail: ApiLegalRegistry | null) {
  if (!detail) {
    return null;
  }

  return {
    ...detail,
    affaireHistorique: (detail.affaireHistorique ?? []).map((entry) => ({
      ...entry,
      document: orNull(entry.document),
    })),
  };
}

/** Main table — filters, sort and page all come from the URL. */
export function useProcedures(search: ProceduresSearch) {
  const params = toListParams(search);

  return useQuery({
    queryKey: queryKeys.procedures.list(params),
    queryFn: async () => {
      const response = await adminAPI.listProcedures(params);
      return { ...response, procedures: response.procedures.map(normalizeProcedure) };
    },
  });
}

/**
 * Hearings marked on the calendar. Deliberately ignores the table filters: the
 * calendar is a navigation aid, so it keeps showing every upcoming hearing even
 * once the table is narrowed down.
 *
 * Gated on the rail being open — the calendar ships closed, and this pulls up to
 * 500 rows purely to place dots.
 */
export function useUpcomingHearings(options?: { enabled?: boolean }) {
  return useQuery({
    enabled: options?.enabled ?? true,
    queryKey: queryKeys.procedures.upcomingHearings,
    queryFn: async () => {
      const response = await adminAPI.listProcedures({
        enrolled: true,
        hearingScope: "upcoming",
        sortBy: "nextHearing",
        sortDir: "asc",
        page: 1,
        pageSize: UPCOMING_HEARINGS_PAGE_SIZE,
      });

      return response.procedures.map(normalizeProcedure);
    },
  });
}

/**
 * Facet values for the filter bar. The two scope toggles are forwarded so each
 * count matches what the table will actually return — without them the court
 * and airline dropdowns promised ten times more rows than the list had.
 */
export function useProcedureFilterOptions(search: ProceduresSearch) {
  const scope = {
    ...(isEnrolledOnly(search) ? { enrolled: true } : {}),
    ...(hidesLongClosed(search) ? {} : { includeLongClosed: true }),
  };

  return useQuery({
    queryKey: queryKeys.procedures.filterOptions(scope),
    queryFn: () => adminAPI.getProcedureFilterOptions(scope),
    staleTime: 5 * 60 * 1000,
  });
}

/** Full Tribunal Digital detail — only fetched once the side panel opens. */
export function useProcedureLegalRegistry(procedureId: string | null) {
  return useQuery({
    queryKey: queryKeys.procedures.legalRegistry(procedureId ?? ""),
    queryFn: async () => normalizeLegalRegistry(await adminAPI.getProcedureLegalRegistry(procedureId ?? "")),
    enabled: procedureId !== null,
  });
}
